import { Api } from "../Api";
import { DataFrame, IDataFrame, Method, Status } from "../DataFrame";
import { ITypedEvent, TypedEvent } from "../TypedEvent";
import { ControllerConfigurationService } from "./Controller/ControllerConfigurationService";
import { IControllerConfigurationService } from "./Controller/IControllerConfigurationService";
import { AuthorizationInfo, BrokerEnvironment, BrokerInfo, ConnectionResult, ConnectionState, IExalusConnectionService, StreamHandler } from "./IExalusConnectionService";
import { ILoggerService } from "./Logging/ILoggerService";
import { LoggerService } from "./Logging/LoggerService";
import { AppState, IAppStateService } from "./IAppStateService";
import { AppStateService } from "./AppStateService";
import { SessionService } from "./Session/SessionService";
import { IUser } from "./Users/IUser";
import { IWebApiCacheService } from "./WebApi/IWebApiCacheService";
import { WebApiCacheService } from "./WebApi/WebApiCacheService";

export class LocalNetworkExalusConnectionService implements IExalusConnectionService {

	SendAndHandleStreamAsync<T>(dataFrame: IDataFrame<any>, streamHandler: StreamHandler<T>, logTransmission: boolean): Promise<void> {
		return Promise.reject(new Error("Streams are not supported over local network connection."));
	}

	private _errorOccuredEvent: ITypedEvent<[sender: string, data: string]> = new TypedEvent<[sender: string, data: string]>();
	private _connectionStateChangedEvent: ITypedEvent<ConnectionState> = new TypedEvent<ConnectionState>();
	private _dataReceivedEvent: ITypedEvent<DataFrame<any>> = new TypedEvent<IDataFrame<any>>();
	private _onMessageReceived: ITypedEvent<string> = new TypedEvent<string>();
	private _log: ILoggerService = Api.Get<ILoggerService>(LoggerService.ServiceName);
	private _controllerConfiguration: IControllerConfigurationService | null = null;
	private _cache: IWebApiCacheService | null = null;
	private _session: SessionService | null = null;
	public static readonly ServiceName: string = "ExalusConnectionService";
	private _logPackets: boolean = false;
	private socket: WebSocket | null = null;
	private _address: string | null = null;
	private _connectedAddress: string | null = null;
	private _port: string = "81";
	private _isOpen: boolean = false;
	private _pin: string | undefined = undefined;
	private _serial: string | undefined = undefined;
	private _auth: AuthorizationInfo | null = null;
	private _initialized: boolean = false;
	private _pingInterval: number = 5000;
	private _pingIntervalId: any | null = null;
	private _lastReceivedPacketTime = Date.now();
	private _disconnectedOnPurpose = false;
	private _lifecycleVersion = 0;
	private _connectTask: Promise<ConnectionResult> | null = null;
	private _connectTaskVersion: number | null = null;
	private _connectAttemptCancel?: () => void;
	private _reconnectTask: Promise<ConnectionResult> | null = null;
	private _reconnectTaskVersion: number | null = null;
	private _restoreTask: Promise<boolean> | null = null;
	private _restoreTaskVersion: number | null = null;

	constructor() {
		Api.Get<IAppStateService>(AppStateService.ServiceName).OnAppStateChanged().Subscribe((newState: AppState) => {
			void (async () => {
				switch (newState) {
					case AppState.ExitedLowPowerMode:
					case AppState.ReturnedFromSuspension:
						if (this._auth !== null && await this.RestoreConnectionAsync())
							await this._session?.RestoreSessionAsync();
						break;
				}
			})().catch(error => {
				this._log.Warning(LocalNetworkExalusConnectionService.ServiceName, `App state handling failed: ${error}`);
			});
		});
	}

	private isLifecycleCurrent(version: number): boolean {
		return version === this._lifecycleVersion && !this._disconnectedOnPurpose;
	}

	private beginExplicitConnection(): number {
		const cancel = this._connectAttemptCancel;
		this._connectAttemptCancel = undefined;
		cancel?.();
		this._lifecycleVersion++;
		this._disconnectedOnPurpose = false;
		return this._lifecycleVersion;
	}

	private clearPingInterval(): void {
		if (this._pingIntervalId !== null) {
			clearInterval(this._pingIntervalId);
			this._pingIntervalId = null;
		}
	}

	private detachAndCloseSocket(socket: WebSocket | null = this.socket): void {
		if (socket == null)
			return;

		if (this.socket === socket) {
			this.socket = null;
			this._connectedAddress = null;
			this._isOpen = false;
			this.clearPingInterval();
		}

		try { socket.close(); } catch {}
	}
	async GetConnectedBrokerInfoAsync(timeoutMs: number | null = 8_000): Promise<BrokerInfo> {
		const controller = await this.GetServerAddressAsync();
		return new BrokerInfo(this._address ?? "", controller ?? "", BrokerEnvironment.Local);
	}

	private Initialize(): void {
		if (this._initialized)
			return;

		this._initialized = true;
		this._controllerConfiguration = Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName);
		this._cache = Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName);
		this._session = Api.Get<SessionService>(SessionService.ServiceName);
	}

	private async checkIfAuthInfoIsCorrectAsync(authInfo: AuthorizationInfo): Promise<boolean> {
		const url = `http://${window.location.hostname}/controller_info`;
		try {
			let res = await fetch(url, { method: 'GET' });

			if (res.ok)
				return await res.text() === `${authInfo.SerialNumber}:${authInfo.PIN}`;
			return false;
		} catch (error) {
			this._log.Warning(LocalNetworkExalusConnectionService.ServiceName, `Authorization check failed: ${error}`);
			return false;
		}
	}


	async PingControllerAsync(): Promise<boolean> {
		if (this.socket?.readyState !== WebSocket.OPEN)
			return false;

		if (this._lastReceivedPacketTime !== null && Date.now() - this._lastReceivedPacketTime < this._pingInterval)
			return false;

		let frame = new DataFrame<any>();
		frame.Resource = "/system/ping";
		frame.Method = Method.Get;

		let result = await this.SendAndWaitForResponseAsync(frame, 2000, false, false)
			.then(() => true)
			.catch(() => false);

		return result;
	}

	SubscribeTo<T>(resourceId: string, handler: { (data: IDataFrame<T>): void; }): () => void {
		let sub = (frame: any) => {
			if (frame.Resource === resourceId)
				handler(frame as IDataFrame<T>);
		};
		this.OnDataReceivedEvent().Subscribe(sub);
		return () => this.OnDataReceivedEvent().Unsubscribe(sub);
	}

	public GetControllerSerialNumber(): string | undefined {
		return this._serial;
	}

	public GetControllerPin(): string | undefined {
		return this._pin;
	}

	SetServersBrokerAddress(address: string): void {
		// Not used for local-network transport.
	}

	SetDefaultPacketsBrokerAddress(address: string): void {
		// Not used for local-network transport.
	}

	GetServerAddressAsync(): Promise<string | null> {
		if (Api.IsRunningFromLocalNetwork())
			this._address = `${window.location.hostname}:${this._port}`;

		return Promise.resolve(this._address);
	}

	ConnectAsync(address: string): Promise<ConnectionResult> {
		const version = this.beginExplicitConnection();
		this.Initialize();
		this._address = address;
		return this.serialisedConnect(address, version);
	}

	private serialisedConnect(address: string, version = this._lifecycleVersion, authorizationInfo?: AuthorizationInfo): Promise<ConnectionResult> {
		if (!this.isLifecycleCurrent(version))
			return Promise.resolve(ConnectionResult.FailedToConnect);

		if (this._connectTask && this._connectTaskVersion === version)
			return this._connectTask;

		this._connectAttemptCancel?.();

		let task: Promise<ConnectionResult>;
		task = this.connectCore(address, version, authorizationInfo).finally(() => {
			if (this._connectTask === task) {
				this._connectTask = null;
				this._connectTaskVersion = null;
				this._connectAttemptCancel = undefined;
			}
		});
		this._connectTask = task;
		this._connectTaskVersion = version;
		return task;
	}

	private async connectCore(address: string, version: number, authorizationInfo?: AuthorizationInfo): Promise<ConnectionResult> {
		if (!this.isLifecycleCurrent(version))
			return ConnectionResult.FailedToConnect;

		this.Initialize();
		const requiredAuthorization = authorizationInfo ?? this._auth;
		const existingSocket = this.socket;
		if (existingSocket?.readyState === WebSocket.OPEN && this._isOpen && this._connectedAddress === address) {
			if (requiredAuthorization != null && !await this.authorizeAsyncCore(requiredAuthorization, version)) {
				this.detachAndCloseSocket(existingSocket);
				return ConnectionResult.FailedToConnect;
			}
			return ConnectionResult.Connected;
		}

		if (existingSocket !== null)
			this.detachAndCloseSocket(existingSocket);
		this._isOpen = false;
		this.clearPingInterval();
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Connecting to the WebSockets server ${address}`);
		(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Connecting);

		return new Promise<ConnectionResult>((resolve) => {
			let socket: WebSocket;
			let settled = false;
			let opened = false;
			let ready = false;

			const settle = (result: ConnectionResult) => {
				if (settled)
					return;
				settled = true;
				resolve(result);
			};

			const isCurrentAttempt = () => this.socket === socket && this.isLifecycleCurrent(version);

			try {
				socket = new WebSocket(`ws://${address}/api`);
			} catch (error) {
				this._log.Warning(LocalNetworkExalusConnectionService.ServiceName, `WebSocket creation failed: ${error}`);
				settle(ConnectionResult.FailedToConnect);
				return;
			}

			this.socket = socket;
			this._connectAttemptCancel = () => {
				settle(ConnectionResult.FailedToConnect);
				this.detachAndCloseSocket(socket);
			};

			socket.onerror = (ev) => {
				if (!isCurrentAttempt()) {
					settle(ConnectionResult.FailedToConnect);
					return;
				}

				this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Error occured in the WebSockets server ${address} info: \n${JSON.stringify(ev)}`);
				this._isOpen = false;
				this.clearPingInterval();
				(this._errorOccuredEvent as TypedEvent<[string, string]>).Invoke(["WebSockets", ev.toString()]);
				settle(ConnectionResult.FailedToConnect);
			};

			socket.onopen = (ev) => {
				void (async () => {
					if (!isCurrentAttempt()) {
						settle(ConnectionResult.FailedToConnect);
						return;
					}

					this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Connected to the WebSockets server ${address} info: \n${JSON.stringify(ev)}`);
					this._isOpen = true;
					this._connectedAddress = address;
					opened = true;
					this._lastReceivedPacketTime = Date.now();

					// Reconnecting the socket is not enough when the controller session
					// must be validated with serial and PIN again.
					if (requiredAuthorization != null && !await this.authorizeAsyncCore(requiredAuthorization, version)) {
						this.detachAndCloseSocket(socket);
						settle(ConnectionResult.FailedToConnect);
						return;
					}

					if (!isCurrentAttempt()) {
						this.detachAndCloseSocket(socket);
						settle(ConnectionResult.FailedToConnect);
						return;
					}

					this._pingIntervalId = setInterval(() => {
						void this.PingControllerAsync().catch(error => {
							this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Ping failed: ${error}`);
						});
					}, this._pingInterval);
					(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Connected);
					ready = true;
					settle(ConnectionResult.Connected);
				})().catch(error => {
					this._log.Warning(LocalNetworkExalusConnectionService.ServiceName, `WebSocket open handling failed: ${error}`);
					this.detachAndCloseSocket(socket);
					settle(ConnectionResult.FailedToConnect);
				});
			};

			socket.onclose = (ev) => {
				if (this.socket !== socket || !this.isLifecycleCurrent(version)) {
					settle(ConnectionResult.FailedToConnect);
					return;
				}

				this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Disconnected from the WebSockets server ${address} info: \n${JSON.stringify(ev)}`);
				this._isOpen = false;
				this._connectedAddress = null;
				this.clearPingInterval();
				settle(ready ? ConnectionResult.Connected : ConnectionResult.FailedToConnect);
				(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Disconnected);

				if (opened && !this._disconnectedOnPurpose && !this._restoreTask && this._auth !== null) {
					(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Reconnecting);
					void this.reconnectAsync().catch(error => {
						this._log.Warning(LocalNetworkExalusConnectionService.ServiceName, `Automatic reconnect failed: ${error}`);
					});
				}
			};

			socket.onmessage = (ev) => {
				if (this.socket !== socket)
					return;

				this._lastReceivedPacketTime = Date.now();
				if (this._logPackets || (window as any)["packets"] === true)
					this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Message received from the WebSockets server ${address}: ${ev.data}`);
				(this._onMessageReceived as TypedEvent<string>).Invoke(ev.data);
				try {
					let frame = JSON.parse(ev.data) as DataFrame<any>;
					this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Received data frame: ${frame.Status}`);
					(this._dataReceivedEvent as TypedEvent<DataFrame<any>>).Invoke(frame);
				} catch (err) {
					this._log.Error(LocalNetworkExalusConnectionService.ServiceName, `Failed to parse message: ${ev.data}`);
				}
			};
		});
	}

	async AuthorizeAsync(authorizationInfo: AuthorizationInfo): Promise<boolean> {
		Api.WorksInContextOf = authorizationInfo.SerialNumber;
		return this.authorizeAsyncCore(authorizationInfo, this._lifecycleVersion);
	}

	private async authorizeAsyncCore(authorizationInfo: AuthorizationInfo, version: number): Promise<boolean> {
		if (!this.isLifecycleCurrent(version))
			return false;

		const socket = this.socket;
		if (socket?.readyState !== WebSocket.OPEN || !this._isOpen)
			return false;

		const valid = await this.checkIfAuthInfoIsCorrectAsync(authorizationInfo);
		if (!valid || !this.isLifecycleCurrent(version) || this.socket !== socket || socket.readyState !== WebSocket.OPEN || !this._isOpen)
			return false;

		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Authorization successful for controller ${authorizationInfo.SerialNumber}`);
		this._pin = authorizationInfo.PIN;
		this._serial = authorizationInfo.SerialNumber;
		this._auth = authorizationInfo;
		(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.ConnectedAndAuthorized);
		return true;
	}

	async ConnectAndAuthorizeAsync(authorizationInfo: AuthorizationInfo): Promise<ConnectionResult> {
		const version = this.beginExplicitConnection();
		Api.WorksInContextOf = authorizationInfo.SerialNumber;
		if (this._auth !== null &&
			(this._auth.SerialNumber !== authorizationInfo.SerialNumber || this._auth.PIN !== authorizationInfo.PIN)) {
			this._auth = null;
			this._serial = undefined;
			this._pin = undefined;
		}
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Connecting and authorizing to the controller ${authorizationInfo.SerialNumber}`);
		let addr = await this.GetServerAddressAsync();

		if (addr == null || !this.isLifecycleCurrent(version))
			return ConnectionResult.FailedToConnectToServer;

		const result = await this.serialisedConnect(addr, version, authorizationInfo);

		if (result !== ConnectionResult.Connected)
			return result;

		return ConnectionResult.Connected;
	}

	async DisconnectAsync(): Promise<void> {
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Disconnecting from the WebSockets server ${this._address}`);
		this._connectAttemptCancel?.();
		this._lifecycleVersion++;
		this._disconnectedOnPurpose = true;
		this._isOpen = false;
		(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Disconnecting);
		this.clearPingInterval();
		this.detachAndCloseSocket();
	}

	IsConnected(): boolean {
		return this._isOpen;
	}

	EnablePacketsLogging(): void {
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Packets logging enabled`);
		this._logPackets = true;
	}

	DisablePacketsLogging(): void {
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Packets logging disabled`);
		this._logPackets = false;
	}

	private SendData(data: DataFrame<any>, logTransmission: boolean) {
		let fr = JSON.stringify(data);

		if (logTransmission)
			this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Sending data frame: ${fr}`);

		if (!this._isOpen || this.socket?.readyState !== WebSocket.OPEN) {
			this._log.Error(LocalNetworkExalusConnectionService.ServiceName, `Failed to send data frame: ${fr}`);
			return false;
		}

		try {
			this.socket.send(fr);
			return true;
		} catch (error) {
			this._log.Warning(LocalNetworkExalusConnectionService.ServiceName, `Failed to send data frame: ${error}`);
			return false;
		}
	}

	SendAsync(dataFrame: IDataFrame<any>): Promise<boolean> {
		if (this._isOpen)
			return Promise.resolve(this.SendData(dataFrame as DataFrame<any>, this._logPackets));
		else
			return Promise.resolve(false);
	}

	SendAndWaitForResponseAsync<T>(dataFrame: IDataFrame<any>, timeout: number, useCache: boolean, logTransmission: boolean = true): Promise<IDataFrame<T>> {
		return this.SendAndWaitForResponseWithRepeatAsync(dataFrame, timeout, useCache, true, logTransmission);
	}

	async SendAndHandleResponseAsync<T>(dataFrame: IDataFrame<any>, timeout: number, dataHandler: (result: IDataFrame<T>) => void, logTransmission: boolean): Promise<void> {
		let timeoutId = 0;
		const startTime = Date.now();

		if (!this.IsConnected()) {
			if (!await this.RestoreConnectionAsync())
				throw new Error("Connection is not established");
		}

		if (dataFrame.Resource !== "/users/user/login")
			await this._session?.WaitForSessionCreationAsync();

		return new Promise(async (resolve, reject) => {
			const enableTimeout = () => window.setTimeout(() => {
				this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
				let errorMessage = `Response timeout, resource: ${dataFrame.Resource} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}`;
				this._log.Error(LocalNetworkExalusConnectionService.ServiceName, errorMessage);
				reject(new Error(errorMessage));
			}, timeout);

			let onReceivedFrame = async (receivedFrame?: IDataFrame<any>) => {
				if (receivedFrame?.TransactionId == dataFrame.TransactionId) {
					window.clearTimeout(timeoutId);
					const difference = Date.now() - startTime;

					if (logTransmission || (window as any)["packets"] === true)
						this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Received response for: ${receivedFrame?.Resource} ${receivedFrame?.Method} id: ${receivedFrame?.TransactionId} in ${difference}ms`);

					if (receivedFrame?.Status === Status.UserIsNotLoggedIn) {
						this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
						(this._session?.OnUserLoggedOutEvent() as TypedEvent<IUser>).Invoke(this._session?.User as IUser);
						try {
							await this._session?.RestoreSessionAsync();
							resolve(await this.SendAndHandleResponseAsync(dataFrame, timeout, dataHandler, logTransmission));
						} catch (error) {
							reject(error);
						}
						return;
					}

					switch (receivedFrame?.Status) {
						case Status.MultiDataResponseStart:
						case Status.MultiDataResponse:
							dataHandler(receivedFrame);
							timeoutId = enableTimeout();
							break;
						case Status.MultiDataResponseStop:
						case Status.FatalError:
						case Status.Error:
							dataHandler(receivedFrame);
							this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
							resolve();
							break;
						default:
							let errorMessage = `Failed to process MultiDataResponse - recived incorrect response status: ${receivedFrame?.Status} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}
                             make sure that the requested endpoint uses MultiDataResponse, otherwise use SendAndWaitForResponseAsync<T> method.`;
							reject(new Error(errorMessage));
							break;
					}
				}
			};

			this._dataReceivedEvent.Subscribe(onReceivedFrame);

			timeoutId = enableTimeout();

			if (!this.SendData(dataFrame, logTransmission)) {
				window.clearTimeout(timeoutId);
				this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
				let errorMessage = `Failed to send request, resource: ${dataFrame.Resource} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}`;
				reject(new Error(errorMessage));
			}
		});
	}

	async SendAndWaitForResponseWithRepeatAsync<T>(dataFrame: IDataFrame<any>, timeout: number, useCache: boolean, repeat = true, logTransmission: boolean = true): Promise<IDataFrame<T>> {
		if (dataFrame.Method === Method.Get && useCache) {
			if (!await this._controllerConfiguration?.DidCofigurationChangeAsync()) {
				let res = this._cache?.GetCache(dataFrame) as IDataFrame<T>;

				if (res != null) {
					return Promise.resolve(res);
				}
			}
		}

		let timeoutId = 0;
		const startTime = Date.now();

		if (!this.IsConnected()) {
			if (!await this.RestoreConnectionAsync())
				throw new Error("Connection is not established");
		}

		if (dataFrame.Resource !== "/users/user/login")
			await this._session?.WaitForSessionCreationAsync();

		return new Promise<IDataFrame<T>>(async (resolve, reject) => {
			let onReceivedFrame = async (receivedFrame?: IDataFrame<any>) => {
				if (receivedFrame?.TransactionId == dataFrame.TransactionId) {
					window.clearTimeout(timeoutId);
					this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
					const difference = Date.now() - startTime;

					if (logTransmission)
						this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Received response for: ${receivedFrame?.Resource} ${receivedFrame?.Method} id: ${receivedFrame?.TransactionId} in ${difference}ms`);

					if (dataFrame.Method === Method.Get && useCache && receivedFrame?.Status !== Status.UserIsNotLoggedIn)
						this._cache?.Cache(receivedFrame as DataFrame<any>);

                    if (receivedFrame?.Status === Status.UserIsNotLoggedIn && repeat) {
                        (this._session?.OnUserLoggedOutEvent() as TypedEvent<IUser>).Invoke(this._session?.User as IUser);
                        try {
                            await this._session?.RestoreSessionAsync();
                            resolve(await this.SendAndWaitForResponseWithRepeatAsync(dataFrame, timeout, useCache, false, logTransmission));
                        } catch (error) {
							reject(error);
						}
						return;
					}

					resolve(receivedFrame as IDataFrame<T>);
				}
			};

			this._dataReceivedEvent.Subscribe(onReceivedFrame);

			timeoutId = window.setTimeout(() => {
				this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
				let errorMessage = `Response timeout, resource: ${dataFrame.Resource} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}`;
				this._log.Error(LocalNetworkExalusConnectionService.ServiceName, errorMessage);
				reject(new Error(errorMessage));
			}, timeout);

			if (!this.SendData(dataFrame, logTransmission)) {
				window.clearTimeout(timeoutId);
				this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
				let errorMessage = `Failed to send request, resource: ${dataFrame.Resource} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}`;
				reject(new Error(errorMessage));
			}
		});
	}

	OnDataReceivedEvent(): ITypedEvent<IDataFrame<any>> {
		return this._dataReceivedEvent;
	}

	OnErrorOccuredEvent(): ITypedEvent<[sender: string, data: string]> {
		return this._errorOccuredEvent;
	}

	OnConnectionStateChangedEvent(): ITypedEvent<ConnectionState> {
		return this._connectionStateChangedEvent;
	}

	GetAuthorizationInfo(): AuthorizationInfo | null {
		return this._auth;
	}

	GetServiceName(): string {
		return LocalNetworkExalusConnectionService.ServiceName;
	}

	private reconnectAsync(version = this._lifecycleVersion): Promise<ConnectionResult> {
		if (!this.isLifecycleCurrent(version))
			return Promise.resolve(ConnectionResult.FailedToConnect);

		if (this._auth === null)
			return Promise.resolve(ConnectionResult.FailedToConnect);

		if (this._reconnectTask && this._reconnectTaskVersion === version)
			return this._reconnectTask;

		let task: Promise<ConnectionResult>;
		task = this.reconnectCore(version).finally(() => {
			if (this._reconnectTask === task) {
				this._reconnectTask = null;
				this._reconnectTaskVersion = null;
			}
		});
		this._reconnectTask = task;
		this._reconnectTaskVersion = version;
		return task;
	}

	private async reconnectCore(version: number): Promise<ConnectionResult> {
		const auth = this._auth;
		if (auth === null)
			return ConnectionResult.FailedToConnect;

		const start = Date.now();
		const maxTime = 30 * 60 * 1000;
		let attempt = 0;

		while (Date.now() - start < maxTime) {
			if (!this.isLifecycleCurrent(version))
				return ConnectionResult.FailedToConnect;

			this.detachAndCloseSocket();
			const addr = await this.GetServerAddressAsync();
			if (addr !== null) {
				const result = await this.serialisedConnect(addr, version);
				if (result === ConnectionResult.Connected && this._auth === auth && await this.authorizeAsyncCore(auth, version))
					return ConnectionResult.Connected;
			}

			if (!this.isLifecycleCurrent(version))
				return ConnectionResult.FailedToConnect;

			const delay = Math.min(30_000, 5_000 * Math.pow(2, attempt++));
			await new Promise(resolve => setTimeout(resolve, delay));
		}

		return ConnectionResult.FailedToConnect;
	}

	private async RestoreConnectionAsync(): Promise<boolean> {
		if (this._disconnectedOnPurpose)
			return false;

		const version = this._lifecycleVersion;

		if (this._reconnectTask && this._reconnectTaskVersion === version)
			return this._reconnectTask.then(result => result === ConnectionResult.Connected && this._auth !== null);

		if (this._restoreTask && this._restoreTaskVersion === version)
			return this._restoreTask;

		let task: Promise<boolean>;
		task = this.restoreConnectionCore(version).finally(() => {
			if (this._restoreTask === task) {
				this._restoreTask = null;
				this._restoreTaskVersion = null;
			}
		});
		this._restoreTask = task;
		this._restoreTaskVersion = version;
		return task;
	}

	private async restoreConnectionCore(version: number): Promise<boolean> {
		const auth = this._auth;
		if (auth === null)
			return false;

		const start = Date.now();
		const maxTime = 30 * 60 * 1000;
		let attempt = 0;

		while (Date.now() - start < maxTime) {
			if (!this.isLifecycleCurrent(version))
				return false;

			if (this.IsConnected()) {
				if (await this.authorizeAsyncCore(auth, version))
					return true;
			} else {
				const addr = await this.GetServerAddressAsync();
				if (addr !== null) {
					const result = await this.serialisedConnect(addr, version);
					if (result === ConnectionResult.Connected && await this.authorizeAsyncCore(auth, version))
						return true;
				}
			}

			if (!this.isLifecycleCurrent(version))
				return false;

			const delay = Math.min(30_000, 5_000 * Math.pow(2, attempt++));
			await new Promise(resolve => setTimeout(resolve, delay));
		}

		return false;
	}
}
